import { useQuery } from '@tanstack/react-query';
import { API_GET_TICKETING_BOARD_VIEWS } from 'constants/appConstants';
import axios from 'constants/axios';
import { useStore } from 'hooks/useStore';
import { exceptionHandler } from 'common/http-exception';
import { getBoardViewsKey } from 'containers/ReportView/_api/queryKeys';
import type { BoardViewsResponse } from './types';

interface UseGetBoardViewsProps {
  id: string | null;
  enabled?: boolean;
  onSuccess?: (data: BoardViewsResponse) => void;
  onError?: (error: unknown) => void;
  showNotification?: boolean; // whether to show notification toast on error or not
}

const API_GET_BOARD_VIEWS = async ({
  id,
  onSuccess,
  onError,
}: Omit<UseGetBoardViewsProps, 'enabled'>) => {
  try {
    const response = await axios.get<BoardViewsResponse>(
      `${API_GET_TICKETING_BOARD_VIEWS(id as string)}`
    );

    if (response.data?.error) {
      throw exceptionHandler(response);
    }

    onSuccess?.(response.data);

    return response.data;
  } catch (error) {
    onError?.(error);

    throw error;
  }
};

export const useGetBoardViewsQuery = ({
  id,
  enabled = true,
  onSuccess,
  onError,
  showNotification = true,
}: UseGetBoardViewsProps) => {
  const addNotificationError = useStore(
    (state) => state.notificationSlice.addNotificationError
  );

  return useQuery({
    enabled: Boolean(id) && enabled,
    queryKey: getBoardViewsKey(id as string),
    queryFn: () =>
      API_GET_BOARD_VIEWS({
        id,
        onSuccess,
        onError: (error) => {
          if (showNotification) {
            addNotificationError({
              message: String(error),
            });
          }

          onError?.(error);
        },
      }),
    retry: false,
  });
};
